import { stacks } from "@dropbox/api-v2-client";
import { Button } from "@dropbox/dig-components/buttons";
import { Card } from "@dropbox/dig-components/card";
import { Text } from "@dropbox/dig-components/typography";
import { Split } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  AiChatLine,
  CloseLine,
  CreateStackLine,
} from "@dropbox/dig-icons/dist/mjs/assets";
import { PAP_Complete_DashShoppingCart } from "@mirage/analytics/events/types/complete_dash_shopping_cart";
import { reportPapEvent } from "@mirage/service-product-logging";
import { useShowStackChooserModal } from "@mirage/stacks/StackChooser/StackChooser";
import i18n from "@mirage/translations";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useResultsShoppingCart } from "./hooks/useResultsShoppingCart";
import styles from "./ResultsShoppingCart.module.css";

interface ResultsShoppingCartProps {
  searchTerm: string;
}

export default function ResultsShoppingCart({
  searchTerm,
}: ResultsShoppingCartProps) {
  const { cartItems, setIsActivated, clearShoppingCart } =
    useResultsShoppingCart();

  const navigate = useNavigate();

  const showStackChooserModal = useShowStackChooserModal(navigate);

  const navigateToChat = useCallback(() => {
    setIsActivated(true);
    navigate("/chat");
    reportPapEvent(
      PAP_Complete_DashShoppingCart({
        selectionLength: cartItems.size,
        dashActionType: "chat",
      }),
    );
  }, [navigate, reportPapEvent, setIsActivated]);

  const addToStack = useCallback(() => {
    const itemsToAdd = Array.from(cartItems.values())
      .filter((item) => {
        if (!item.searchResult.url) {
          return false;
        }
        return true;
      })
      .map((item) => ({
        url: item.searchResult.url ?? "",
        metadata_title: item.searchResult.title,
        branded_type: item.searchResult.brandedType ?? undefined,
        id_3p: item.searchResult.id3p ?? undefined,
      }));
    showStackChooserModal({
      callback: (stack?: stacks.Stack) => {
        if (stack) {
          clearShoppingCart(true);
          reportPapEvent(
            PAP_Complete_DashShoppingCart({
              selectionLength: cartItems.size,
              dashActionType: "add_to_stack",
            }),
          );
        }
      },
      searchTerm: searchTerm,
      url: itemsToAdd[0].url,
      items: Array.from(cartItems.values())
        .filter((item) => {
          if (!item.searchResult.url) {
            return false;
          }
          return true;
        })
        .map((item) => ({
          url: item.searchResult.url ?? "",
          metadata_title: item.searchResult.title,
          branded_type: item.searchResult.brandedType ?? undefined,
          id_3p: item.searchResult.id3p ?? undefined,
        })),
    });
  }, [showStackChooserModal, cartItems, reportPapEvent]);

  if (cartItems.size === 0) {
    return null;
  }
  return (
    <div className={styles.ResultsShoppingCartContainer}>
      <Card borderRadius="XLarge" className={styles.ResultsShoppingCardCard}>
        <Card.Content>
          <Split gap="20" alignY="center" paddingX="12" paddingY="8">
            <Split.Item>
              <Text color="standard" size="large" isBold>
                {i18n.t("shopping_cart_selected")}
              </Text>
              &nbsp;
              <Text>{cartItems.size}</Text>
            </Split.Item>
            <Split.Item>
              <Split gap="10">
                <Split.Item>
                  <Text />
                </Split.Item>
                <Split.Item>
                  <Button
                    variant="outline"
                    onClick={addToStack}
                    withIconStart={<UIIcon src={CreateStackLine} />}
                  >
                    {i18n.t("add_to_stack")}
                  </Button>
                </Split.Item>
                <Split.Item>
                  <Button
                    variant="outline"
                    onClick={navigateToChat}
                    withIconStart={<UIIcon src={AiChatLine} />}
                  >
                    {i18n.t("chat_label")}
                  </Button>
                </Split.Item>
                <Split.Item>
                  <Button variant="outline" onClick={() => clearShoppingCart()}>
                    <UIIcon src={CloseLine} />
                  </Button>
                </Split.Item>
              </Split>
            </Split.Item>
          </Split>
        </Card.Content>
      </Card>
    </div>
  );
}
